<template>
  <div class="page-container">
    <article-header
      class="fixed-header"
      :article-tree="articleTree"
      :current-article="currentArticle"
      :headings="headings"
      @article-click="handleHeaderArticleClick"
    />

    <!-- 桌面端侧边栏 -->
    <div class="sidebar" :class="{ 'collapsed': sidebarCollapsed }" v-if="!isMobile">
      <!-- 收起/展开按钮 -->
      <div class="sidebar-toggle" @click="toggleSidebar">
        <a-icon :type="sidebarCollapsed ? 'menu-unfold' : 'menu-fold'" />
      </div>

      <div class="sidebar-content" v-show="!sidebarCollapsed">
        <a-menu
          mode="inline"
          :selected-keys="selectedKeys"
          :open-keys="openKeys"
          @openChange="handleOpenChange"
          @select="handleMenuSelect"
          :style="{ height: '100%', borderRight: 0 }"
        >
          <template v-for="item in articleTree">
            <a-sub-menu v-if="item.children && item.children.length" :key="item.id">
              <template #title>{{ item.title }}</template>
              <template v-for="child in item.children">
                <a-sub-menu v-if="child.children && child.children.length" :key="child.id">
                  <template #title>{{ child.title }}</template>
                  <template v-for="grandchild in child.children">
                    <a-sub-menu v-if="grandchild.children && grandchild.children.length" :key="grandchild.id">
                      <template #title>{{ grandchild.title }}</template>
                      <a-menu-item
                        v-for="greatGrandchild in grandchild.children"
                        :key="greatGrandchild.id"
                        @click="handleArticleClick(greatGrandchild)"
                      >
                        {{ greatGrandchild.title }}
                      </a-menu-item>
                    </a-sub-menu>
                    <a-menu-item
                      v-else
                      :key="grandchild.id"
                      @click="handleArticleClick(grandchild)"
                    >
                      {{ grandchild.title }}
                    </a-menu-item>
                  </template>
                </a-sub-menu>
                <a-menu-item
                  v-else
                  :key="child.id"
                  @click="handleArticleClick(child)"
                >
                  {{ child.title }}
                </a-menu-item>
              </template>
            </a-sub-menu>
            <a-menu-item
              v-else
              :key="item.id"
              @click="handleArticleClick(item)"
            >
              {{ item.title }}
            </a-menu-item>
          </template>
        </a-menu>
      </div>
    </div>

    <main class="main-content" :class="{ 'mobile': isMobile, 'sidebar-collapsed': sidebarCollapsed }">
      <div class="content-wrapper">
        <div class="content">
          <!-- 移动端本页总览 -->
          <div v-if="isMobile && headings.length > 0" class="mobile-toc-section">
            <div class="mobile-toc-title" @click="toggleToc">
              <span>本页总览</span>
              <span class="toc-toggle-icon" :class="{ 'expanded': tocExpanded }">
                <a-icon type="down" />
              </span>
            </div>
            <div class="mobile-toc-container" :class="{ 'collapsed': !tocExpanded }">
              <div
                v-for="heading in headings"
                :key="heading.id"
                class="mobile-toc-item"
                :class="`level-${heading.level}`"
                @click="handleTocClick(heading)"
              >
                <span class="toc-text">{{ heading.title }}</span>
              </div>
            </div>
          </div>

          <div v-if="loading" class="loading-content">
            <a-spin />
          </div>
          <template v-else-if="currentArticle">
            <h1 :id="'article-' + currentArticle.id" class="article-title">
              {{ currentArticle.title }}
            </h1>
            <!--            <div class="article-version-info" v-if="currentArticle.title.includes('用户服务协议')">-->
            <!--              <div>-->
            <!--                当前版本号：【{{ currentArticle.version}}】-->
            <!--              </div>-->
            <!--              <div>-->
            <!--                发布日期：【{{ currentArticle.updateTime.substr(0,4)+'年'+currentArticle.updateTime.substr(5,2)+'月'+currentArticle.updateTime.substr(8,2)+'日'}}】-->
            <!--              </div>-->
            <!--              <div>-->
            <!--                生效时期：【{{ currentArticle.updateTime.substr(0,4)+'年'+currentArticle.updateTime.substr(5,2)+'月'+currentArticle.updateTime.substr(8,2)+'日' }}】-->
            <!--              </div>-->
            <!--            </div>-->
            <div class="article-content" v-if="currentArticle.content" v-html="currentArticle.content"></div>
            <div class="empty-content" v-else>暂无内容</div>
          </template>
          <div v-else class="empty-content">
            请选择左侧菜单查看内容
          </div>
        </div>

        <!-- 桌面端右侧导航 -->
        <aside class="section-nav" v-if="currentArticle && showNav && headings.length">
          <div class="nav-wrapper">
            <!-- <div class="nav-title">目录</div> -->
            <a-anchor
              ref="anchorComponent"
              :get-container="getContainer"
              @click="handleAnchorClick"
              :target-offset="80"
            >
              <a-anchor-link
                v-for="heading in headings"
                :key="heading.id"
                :href="`#${heading.id}`"
                :title="heading.title"
              />
            </a-anchor>
          </div>
        </aside>
      </div>
    </main>
  </div>
</template>

<script>
import { getArticleTreeList, getArticleDetail } from '@/api/api'
import { marked } from 'marked'
import articleHeader from './articleHeader.vue'
import _ from 'lodash'

// 配置 marked 解析器 - 简化版本，无代码高亮
marked.setOptions({
  breaks: true,
  gfm: true,
  sanitize: false,
  async: false
})
export default {
  name: 'ArticleListPage',
  components: {
    articleHeader
  },
  data() {
    return {
      openKeys: [], // 展开的菜单项
      articleTree: [], // 文章树形数据
      selectedKeys: [], // 选中的菜单项
      currentArticle: null, // 当前文章
      defaultArticleId: '', // 默认文章ID
      isMarkdown: false, // 是否为markdown内容
      loading: true, // 加载状态
      headings: [], // 添加headings数组存储标题导航
      showNav: this.$isServer ? false : window.innerWidth >= 1200, // 是否显示导航
      isMobile: this.$isServer ? false : window.innerWidth <= 1024, // 是否为移动设备，统一断点为1024px
      tocExpanded: false, // 本页总览默认展开
      sidebarCollapsed: false, // 侧边栏收起状态
      activeHeading: '', // 当前激活的标题ID
      isNavigating: false, // 是否正在导航中
      navigationTimer: null, // 导航定时器
      lastClickTime: 0, // 最后点击时间
      clickDebounceDelay: 300 // 点击防抖延迟(ms)
    }
  },

  watch: {
    // 监听文章树数据变化
    articleTree: {
      immediate: true,
      handler(tree) {
        if (tree.length && this.$route.query.id) {
          this.$nextTick(() => {
            this.setMenuStates(this.$route.query.id)
          })
        }
      }
    },
    // 监听路由变化
    $route: {
      immediate: true,
      handler(to) {
        const { id } = to.query
        if (id) {
          this.$nextTick(async () => {
            // 如果当前文章ID与路由ID不一致，则加载新文章
            if (!this.currentArticle || String(this.currentArticle.id) !== String(id)) {
              await this.loadArticle(id)
            } else {
              // 如果文章已经加载，只更新菜单状态
              this.setMenuStates(id)
            }
          })
        }
      }
    }
  },

  async mounted() {
    await this.fetchArticleTree()
    console.log('mounted===>globalDataArray:' + this.globalDataArray)

    const { id } = this.$route.query
    if (id) {
      await this.loadArticle(id)
    } else if (this.articleTree.length > 0) {
      const firstArticle = this.findFirstArticle(this.articleTree)
      if (firstArticle) {
        await this.loadArticle(firstArticle.id)
      }
    }

    if (this.$isServer) return

    // 添加窗口大小变化监听
    window.addEventListener('resize', this.handleResize)
    this.handleResize() // 初始调用一次

    // 添加滚动监听
    window.addEventListener('scroll', this.handleScrollThrottle)
  },

  beforeUnmount() {
    if (this.$isServer) return
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScrollThrottle)

    // 清理导航定时器
    if (this.navigationTimer) {
      clearTimeout(this.navigationTimer)
      this.navigationTimer = null
    }

    // 指示器更新定时器已移除
    // if (this.indicatorUpdateTimer) {
    //   clearTimeout(this.indicatorUpdateTimer)
    //   this.indicatorUpdateTimer = null
    // }

    // 清理滚动动画帧
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame)
    }
  },

  methods: {
    // 递归查找所有父级ID（关键方法）
    findParentIds(targetId, tree, path = []) {
      // 添加安全检查
      if (!tree || !Array.isArray(tree) || !targetId) {
        return null
      }

      for (const node of tree) {
        // 检查节点数据的完整性
        if (!node || typeof node.id === 'undefined') {
          continue
        }

        // 找到目标节点时返回路径
        if (String(node.id) === String(targetId)) return path

        // 递归搜索子节点
        if (node.children && Array.isArray(node.children) && node.children.length) {
          const found = this.findParentIds(
            targetId,
            node.children,
            [...path, node.id] // 记录当前路径
          )
          if (found) return found
        }
      }
      return null
    },
    // 设置菜单状态
    setMenuStates: _.debounce(function(targetId) {
      // 检查文章树是否存在且有效
      if (!this.articleTree || this.articleTree.length === 0) {
        console.warn('Article tree is empty, cannot set menu states')
        return
      }

      // 检查目标ID是否存在于树形数据中
      const nodeExists = this.findNodeById(targetId, this.articleTree)
      if (!nodeExists) {
        console.warn(`Article with ID ${targetId} not found in tree, redirecting to first article`)
        // 如果文章不存在，重定向到第一篇文章
        this.redirectToFirstArticle()
        return
      }

      // 获取所有需要展开的父级ID
      const parentIds = this.findParentIds(targetId, this.articleTree) || []

      // 设置展开状态（保留用户手动展开的状态）
      this.openKeys = [...new Set([...this.openKeys, ...parentIds])]

      // 设置选中状态
      this.selectedKeys = [String(targetId)]
    }, 50),
    // 重定向到第一篇文章的方法
    async redirectToFirstArticle() {
      if (this.articleTree.length > 0) {
        const firstArticle = this.findFirstArticle(this.articleTree)
        if (firstArticle) {
          console.log('Redirecting to first article:', firstArticle.title)
          // 直接加载第一篇文章，这样可以确保内容和菜单状态都更新
          await this.loadArticle(firstArticle.id)
          return
        }
      }

      // 如果没有找到任何文章，清空当前文章并显示提示
      this.currentArticle = null
      this.selectedKeys = []
      this.loading = false
    },
    // 处理菜单展开状态变化
    handleOpenChange(openKeys) {
      // 更新展开状态
      this.openKeys = openKeys
    },
    // 获取文章树形列表
    async fetchArticleTree() {
      try {
        const res = await getArticleTreeList()
        if (res.success && res.result) {
          // 验证数据结构
          if (Array.isArray(res.result)) {
            this.articleTree = res.result
          } else {
            console.error('Article tree data is not an array:', res.result)
            this.articleTree = []
          }
        } else {
          console.warn('Failed to get article tree:', res.message || 'Unknown error')
          this.articleTree = []
        }
      } catch (error) {
        console.error('获取文章列表失败:', error)
        this.articleTree = []

        // 显示用户友好的错误信息
        if (!this.$isServer) {
          this.$message.error('获取文章列表失败，请刷新页面重试')
        }
      }
    },

    // 文章点击处理
    async handleArticleClick(article) {
      // 添加安全检查
      if (!article || typeof article.id === 'undefined' || typeof article.isDirectory === 'undefined') {
        console.error('Invalid article object:', article)
        return
      }

      // 只有非目录项才加载内容
      if (article.isDirectory === 0) {
        await this.loadArticle(article.id)
      } else {
        // 如果是目录节点，只切换展开状态
        const index = this.openKeys.indexOf(article.id)
        if (index === -1) {
          this.openKeys = [...this.openKeys, article.id]
        } else {
          const newOpenKeys = [...this.openKeys]
          newOpenKeys.splice(index, 1)
          this.openKeys = newOpenKeys
        }
      }
    },

    parseMarkdown(content) {
      try {
        if (!content || typeof content !== 'string') {
          console.warn('parseMarkdown: Invalid content type:', typeof content)
          return '<p>内容为空</p>'
        }

        console.log('parseMarkdown: Processing content length:', content.length)
        console.log('parseMarkdown: Content preview:', content.substring(0, 100) + '...')

        const parsed = marked.parse(content)
        if (!parsed || parsed.trim() === '') {
          console.warn('parseMarkdown: Empty result from marked.parse')
          return `<div class="markdown-fallback"><pre>${content}</pre></div>`
        }

        console.log('parseMarkdown: Successfully parsed')
        return parsed
      } catch (e) {
        console.error('parseMarkdown: 解析失败:', e)
        console.error('parseMarkdown: Content that failed:', content.substring(0, 200))
        return `<div class="markdown-error">
          <p class="error">Markdown解析异常</p>
          <details>
            <summary>查看原始内容</summary>
            <pre>${content}</pre>
          </details>
        </div>`
      }
    },

    // 查找第一篇可读文章
    findFirstArticle(tree) {
      // 添加安全检查
      if (!tree || !Array.isArray(tree)) {
        return null
      }

      for (const item of tree) {
        // 检查节点数据的完整性
        if (!item || typeof item.isDirectory === 'undefined') {
          continue
        }

        if (item.isDirectory === 0) {
          return item
        }
        if (item.children && Array.isArray(item.children) && item.children.length) {
          const found = this.findFirstArticle(item.children)
          if (found) return found
        }
      }
      return null
    },

    // 根据ID在树中查找节点
    findNodeById(id, tree) {
      // 添加安全检查
      if (!tree || !Array.isArray(tree) || !id) {
        return null
      }

      for (const item of tree) {
        // 检查节点数据的完整性
        if (!item || typeof item.id === 'undefined') {
          continue
        }

        if (String(item.id) === String(id)) {
          return item
        }
        if (item.children && Array.isArray(item.children) && item.children.length) {
          const found = this.findNodeById(id, item.children)
          if (found) return found
        }
      }
      return null
    },

    // 加载文章内容
    async loadArticle(id) {
      this.loading = true
      try {
        const res = await getArticleDetail('id=' + id)
        if (res.success && res.result) {
          const article = res.result

          // 验证文章数据的完整性
          if (!article.id || !article.title) {
            console.error('Article data is incomplete:', article)
            this.handleArticleNotFound(id)
            return
          }

          // 检查是否为目录项
          if (article.isDirectory === 1) {
            console.warn('Directory item detected, redirecting to first content:', article.title)

            // 在树形数据中找到这个目录节点
            const directoryNode = this.findNodeById(id, this.articleTree)
            if (directoryNode && directoryNode.children && directoryNode.children.length) {
              // 找到该目录下的第一个内容项
              const firstContentItem = this.findFirstArticle(directoryNode.children)
              if (firstContentItem) {
                // 递归调用，加载第一个内容项
                await this.loadArticle(firstContentItem.id)
                return
              }
            }

            // 如果没找到内容项，设置当前文章为目录项并显示无内容提示
            console.warn('No content found in directory:', article.title)
            this.currentArticle = {
              id: id,
              title: article.title,
              content: '<div class="empty-directory-content">No Data</div>',
              isDirectory: 1
            }
            this.selectedKeys = [String(id)]
            this.setMenuStates(id)
            this.loading = false
            return
          } else {
            if (article.linkUrl) {
              // window.open(article.linkUrl, '_blank')
              // 打开链接后，重定向到第一篇文章
              // await this.redirectToFirstArticle()
              window.location.href = article.linkUrl
              return
            }
          }

          this.currentArticle = article
          this.selectedKeys = [String(id)]
          if (this.$isServer) return

          // 检测内容类型并解析
          const originalContent = this.currentArticle.content || ''
          console.log('loadArticle: Original content length:', originalContent.length)
          console.log('loadArticle: Content preview:', originalContent.substring(0, 200) + '...')

          this.isMarkdown = this.detectContentType(originalContent)
          console.log('loadArticle: Is markdown detected:', this.isMarkdown)

          // 临时调试：可以通过URL参数强制启用markdown解析
          const forceMarkdown = this.$route.query.forceMarkdown === 'true'
          if (forceMarkdown) {
            console.log('loadArticle: Force markdown enabled via URL parameter')
            this.isMarkdown = true
          }

          if (this.isMarkdown) {
            console.log('loadArticle: Processing as markdown')
            this.currentArticle.content = this.parseMarkdown(originalContent)
          } else {
            console.log('loadArticle: Processing as HTML/plain text')
            // 如果不是markdown，检查是否是HTML
            if (originalContent.includes('<') && originalContent.includes('>')) {
              console.log('loadArticle: Content appears to be HTML')
            } else {
              console.log('loadArticle: Content appears to be plain text')
              // 对于纯文本，至少保留换行符
              this.currentArticle.content = originalContent.replace(/\n/g, '<br>')
            }
          }

          if (!this.$isServer) {
            this.$nextTick(() => {
              const content = document.querySelector('.article-content')
              if (content) {
                const headings = content.querySelectorAll('h1, h2, h3, h4, h5, h6')
                headings.forEach((el, index) => {
                  el.id = `heading-${index}` // 统一设置标题ID
                })
                this.parseHeadings() // 基于实际DOM元素解析导航

                // 重新初始化滚动状态
                this.$nextTick(() => {
                  if (this.headings.length > 0) {
                    this.activeHeading = this.headings[0].id
                    this.updateAnchorActiveState(this.headings[0].id)

                    // 指示器已隐藏，无需初始化位置
                    // setTimeout(() => {
                    //   this.updateIndicatorPosition(this.headings[0].id)
                    // }, 100)
                  }
                })
              }
            })
          }

          this.$router.push({
            query: { ...this.$route.query, id }
          })
        } else {
          // 处理请求成功但没有数据的情况（文章已被删除）
          console.warn('Article not found or deleted:', id)
          this.handleArticleNotFound(id)
        }
      } catch (error) {
        console.error('加载文章失败:', error)
        this.handleArticleNotFound(id)
      } finally {
        this.loading = false
      }

      // 只有在文章存在时才设置菜单状态
      if (this.currentArticle) {
        this.setMenuStates(id)
      }

      this.$nextTick(() => {
        this.adjustNavigationPosition()
        // 添加内容变化监听
        if (!this.$isServer) {
          const content = document.querySelector('.article-content')
          if (content) {
            new ResizeObserver(this.adjustNavigationPosition).observe(content)
          }
        }
      })
    },

    // 处理文章未找到的情况
    handleArticleNotFound(id) {
      console.warn(`Article with ID ${id} not found or deleted`)

      // 检查文章是否在树形数据中存在
      const nodeExists = this.findNodeById(id, this.articleTree)
      if (!nodeExists) {
        // 如果文章不在树形数据中，说明已被删除，重定向到第一篇文章
        this.redirectToFirstArticle()
      } else {
        // 如果在树形数据中存在但获取详情失败，显示错误信息
        this.currentArticle = {
          id: id,
          title: '文章加载失败',
          content: '<div class="error-content">该文章可能已被删除或暂时无法访问，请刷新页面或选择其他文章。</div>'
        }
        this.selectedKeys = [String(id)]
      }
    },

    // 检测内容类型是否为Markdown
    detectContentType(content) {
      // 添加安全检查
      if (!content || typeof content !== 'string') {
        return false
      }

      // 增强的markdown检测模式
      const markdownPatterns = [
        /^#+ /m, // 标题 (多行模式)
        /^#{1,6}\s+/m, // 标题的另一种写法
        /\[.*?\]\(.*?\)/, // 链接
        /\*\*.*?\*\*/, // 粗体
        /\*.*?\*/, // 斜体
        /`.*?`/, // 行内代码
        /```[\s\S]*?```/, // 代码块
        /^\s*\-\s+/m, // 无序列表
        /^\s*\*\s+/m, // 无序列表
        /^\s*\+\s+/m, // 无序列表
        /^\s*\d+\.\s+/m, // 有序列表
        /^\s*>\s+/m, // 引用
        /^\s*\|\s*.*?\s*\|/m, // 表格
        /^---+$/m, // 分隔线
        /^\s*\n```/m, // 代码块开始
        /~~.*?~~/, // 删除线
        /==.*?==/, // 高亮
        /^\s{4,}/m, // 代码缩进
        /\n\s*\n/ // 多个空行
      ]

      // 检查是否包含markdown特征
      const hasMarkdownFeatures = markdownPatterns.some(pattern => pattern.test(content))

      // 如果没有检测到markdown特征，但内容包含换行符且不是HTML，也可能是markdown纯文本
      if (!hasMarkdownFeatures && content.includes('\n') && !/<[^>]*>/g.test(content)) {
        // 检查是否是纯文本格式的markdown（简单的换行格式）
        const lines = content.split('\n')
        const hasStructure = lines.some(line => {
          const trimmed = line.trim()
          return trimmed.startsWith('#') ||
                 trimmed.startsWith('-') ||
                 trimmed.startsWith('*') ||
                 trimmed.startsWith('+') ||
                 /^\d+\./.test(trimmed)
        })
        return hasStructure
      }

      return hasMarkdownFeatures
    },

    // 获取滚动容器
    getContainer() {
      if (this.$isServer) return null
      return document.querySelector('.content-wrapper')
    },

    // 解析文章内容中的标题
    parseHeadings() {
      if (this.$isServer) return

      this.headings = []
      const content = document.querySelector('.article-content')
      if (!content) return

      const headingElements = content.querySelectorAll('h1, h2, h3, h4, h5, h6')
      headingElements.forEach((el) => {
        this.headings.push({
          id: el.id,
          title: el.textContent,
          level: parseInt(el.tagName.charAt(1))
        })
      })

      // 解析完标题后，初始化激活状态
      this.$nextTick(() => {
        if (this.headings.length > 0) {
          // 设置第一个标题为默认激活状态
          this.activeHeading = this.headings[0].id
          this.updateAnchorActiveState(this.headings[0].id)

          // 触发一次滚动处理以确保状态正确
          this.handleScroll()
        }
      })
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.$isServer) return

      // 更新是否为移动设备
      this.isMobile = window.innerWidth <= 1024

      // 更新是否显示右侧导航
      const newShowNav = window.innerWidth >= 1200
      if (!this.showNav && newShowNav && this.currentArticle) {
        // 当从小屏切换到大屏时重新解析标题
        this.$nextTick(() => {
          this.parseHeadings(this.currentArticle.content)
        })
      }
      this.showNav = newShowNav

      // 调整导航位置
      this.adjustNavigationPosition()
    },

    // 调整导航位置
    adjustNavigationPosition() {
      // 服务端渲染时直接返回
      if (this.$isServer) return

      this.$nextTick(() => {
        const nav = document.querySelector('.section-nav')
        if (!nav) return

        const windowHeight = window.innerHeight
        const navHeight = nav.scrollHeight
        const newTop = Math.max(0, (windowHeight - navHeight) / 2)

        // 动态设置位置
        nav.style.top = `${newTop}px`
      })
    },

    // 添加 select 事件处理函数
    handleMenuSelect(selection) {
      this.selectedKeys = selection.selectedKeys
    },

    handleHeaderArticleClick(article) {
      // 处理来自头部菜单的文章点击事件
      this.handleArticleClick(article)
    },

    toggleToc() {
      this.tocExpanded = !this.tocExpanded
    },

    handleTocClick(heading) {
      // 处理章节点击事件，滚动到对应位置
      if (this.$isServer) return

      // 防抖处理：如果点击间隔太短，延迟处理
      const currentTime = Date.now()
      if (currentTime - this.lastClickTime < this.clickDebounceDelay) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏸️ 频繁点击检测，延迟处理')
        }

        // 清除之前的定时器
        if (this.navigationTimer) {
          clearTimeout(this.navigationTimer)
        }

        // 延迟执行真正的点击处理
        this.navigationTimer = setTimeout(() => {
          this.performNavigation(heading)
        }, this.clickDebounceDelay)
        return
      }

      this.lastClickTime = currentTime
      this.performNavigation(heading)
    },

    // 执行实际的导航操作
    performNavigation(heading) {
      const element = document.getElementById(heading.id)
      if (!element) return

      if (process.env.NODE_ENV === 'development') {
        console.log(`📱 移动端目录点击: ${heading.title}`)
      }

      // 清除所有之前的定时器和状态
      if (this.navigationTimer) {
        clearTimeout(this.navigationTimer)
        this.navigationTimer = null
      }

      // 立即更新激活状态
      this.activeHeading = heading.id
      this.isNavigating = true

      // 立即清理并更新视觉状态
      this.clearAllActiveStates()
      this.updateAnchorActiveState(heading.id)

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })

      // 收起移动端目录
      if (this.isMobile) {
        this.tocExpanded = false
      }

      // 设置较短的恢复时间，减少状态锁定时间
      this.navigationTimer = setTimeout(() => {
        this.isNavigating = false
        this.navigationTimer = null
        // 最终确保状态正确
        this.updateAnchorActiveState(heading.id)
      }, 800)
    },

    getSectionPrefix(level) {
      // 去掉章节导航前面的数字，返回空字符串
      return ''
    },

    // 切换侧边栏显示/隐藏
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },

    // 节流滚动监听（参考 organize/index.vue 实现）
    handleScrollThrottle: function() {
      // 只有在非导航动画期间才处理滚动事件
      if (!this.isNavigating) {
        cancelAnimationFrame(this.scrollFrame)
        this.scrollFrame = requestAnimationFrame(this.handleScroll)
      } else if (process.env.NODE_ENV === 'development') {
        console.log('⏸️ 导航中，跳过滚动处理')
      }
    },

    // 处理滚动事件（修复长内容章节的激活问题）
    handleScroll() {
      if (this.$isServer || !this.headings.length) return

      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const viewportHeight = window.innerHeight
      const viewportTop = scrollTop
      const viewportCenter = scrollTop + viewportHeight / 2

      let currentHeading = ''

      // 策略：找到当前滚动位置所属的章节
      // 1. 遍历所有标题，找到当前滚动位置处于哪个章节的内容区域
      for (let i = 0; i < this.headings.length; i++) {
        const currentElement = document.getElementById(this.headings[i].id)
        if (!currentElement) continue

        const currentTop = currentElement.offsetTop

        // 获取下一个标题的位置，如果没有下一个标题，则使用文档底部
        let nextTop = document.documentElement.scrollHeight
        if (i < this.headings.length - 1) {
          const nextElement = document.getElementById(this.headings[i + 1].id)
          if (nextElement) {
            nextTop = nextElement.offsetTop
          }
        }

        // 检查当前滚动位置是否在这个章节的范围内
        // 使用一定的偏移量避免边界问题
        const headerOffset = 80 // 给标题预留的偏移量，考虑固定头部高度

        // 调试信息（临时启用来验证修复效果）
        if (process.env.NODE_ENV === 'development') {
          console.log(`检查章节 ${i}: ${this.headings[i].title}, 范围: ${currentTop - headerOffset} - ${nextTop - headerOffset}, 当前位置: ${viewportCenter}`)
        }

        if (viewportCenter >= (currentTop - headerOffset) && viewportCenter < (nextTop - headerOffset)) {
          currentHeading = this.headings[i].id
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ 匹配到章节: ${this.headings[i].title}`)
          }
          break
        }
      }

      // 如果没有找到匹配的章节，使用备用策略
      if (!currentHeading) {
        // 备用策略1：找到当前滚动位置之前最近的标题
        for (let i = this.headings.length - 1; i >= 0; i--) {
          const element = document.getElementById(this.headings[i].id)
          if (element) {
            const elementTop = element.offsetTop
            // 如果标题位置在当前滚动位置之前（或稍微之后一点），则认为用户在阅读这个章节
            if (elementTop <= viewportCenter + 100) {
              currentHeading = this.headings[i].id
              if (process.env.NODE_ENV === 'development') {
                console.log(`🔄 备用策略匹配到章节: ${this.headings[i].title}`)
              }
              break
            }
          }
        }

        // 备用策略2：如果用户在页面最顶部，使用第一个标题
        if (!currentHeading && viewportTop < 200 && this.headings.length > 0) {
          currentHeading = this.headings[0].id
        }

        // 备用策略3：如果用户在页面最底部，使用最后一个标题
        if (!currentHeading && (viewportTop + viewportHeight) >= (document.documentElement.scrollHeight - 100)) {
          currentHeading = this.headings[this.headings.length - 1].id
        }
      }

      // 更新激活状态（只有在非导航状态下才更新）
      if (currentHeading && currentHeading !== this.activeHeading && !this.isNavigating) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`🎯 滚动激活状态更新: ${this.activeHeading} → ${currentHeading}`)
        }
        this.activeHeading = currentHeading
        this.updateAnchorActiveStateForScroll(currentHeading)
      } else if (this.isNavigating && process.env.NODE_ENV === 'development') {
        console.log(`⏸️ 导航中，跳过滚动激活状态更新`)
      }
    },

    // 更新锚点激活状态（点击触发）
    updateAnchorActiveState(headingId) {
      if (this.$isServer) return

      // 更新 URL hash - 使用静默更新避免页面跳转
      const newHash = `#${headingId}`
      if (window.location.hash !== newHash) {
        // 阻止默认的锚点跳转行为
        window.history.replaceState(null, null, newHash)
      }

      // 方法1：通过组件实例更新（推荐方式）
      this.$nextTick(() => {
        if (this.$refs.anchorComponent) {
          // 触发 ant-anchor 组件内部的状态更新
          // 这会同时更新类名和指示器位置
          try {
            // 调用组件的内部方法来更新当前激活的链接
            const anchorInstance = this.$refs.anchorComponent
            if (anchorInstance && anchorInstance.handleScrollTo) {
              // 模拟点击来触发正确的内部状态更新
              const targetLink = document.querySelector(`.section-nav a[href="#${headingId}"]`)
              if (targetLink) {
                // 触发组件内部的激活状态更新
                anchorInstance.setState && anchorInstance.setState({
                  activeLink: `#${headingId}`
                })
              }
            }
          } catch (error) {
            // 如果组件方法不可用，回退到手动更新
            console.log('使用手动方式更新anchor状态')
          }
        }

        // 方法2：手动更新DOM（备用方式，确保指示器显示）
        this.updateAnchorManually(headingId)
      })
    },

    // 更新锚点激活状态（滚动触发，带防抖）
    updateAnchorActiveStateForScroll(headingId) {
      if (this.$isServer) return

      // 直接执行滚动更新，不需要指示器防抖
      this.performScrollUpdate(headingId)
    },

    // 执行滚动状态更新
    performScrollUpdate(headingId) {
      // 更新 URL hash（静默更新，不触发滚动）
      const newHash = `#${headingId}`
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, null, newHash)
      }

      // 更新DOM状态（不包含指示器）
      this.updateAnchorTextStateOnly(headingId)
    },

    // 手动更新锚点状态（确保小蓝点显示）
    updateAnchorManually(headingId) {
      // 首先清理所有激活状态
      this.clearAllActiveStates()

      // 添加当前激活状态
      let activatedCount = 0
      const allLinks = document.querySelectorAll('.section-nav .ant-anchor-link')
      allLinks.forEach(el => {
        const titleEl = el.querySelector('.ant-anchor-link-title')
        const href = titleEl?.getAttribute('href')
        if (href === `#${headingId}`) {
          el.classList.add('ant-anchor-link-active')
          activatedCount++
        }
      })

      // 指示器已隐藏，无需更新位置
      // this.updateIndicatorPosition(headingId)

      // 调试信息：确保只有一个激活状态
      if (process.env.NODE_ENV === 'development') {
        console.log(`✨ 激活状态更新完成: ${headingId}, 激活数量: ${activatedCount}`)

        // 验证是否还有其他激活状态
        const remainingActive = document.querySelectorAll('.section-nav .ant-anchor-link-active')
        if (remainingActive.length !== 1) {
          console.warn(`⚠️ 检测到多个激活状态: ${remainingActive.length}`)
        }
      }
    },

    // 平滑更新锚点状态（滚动专用）
    updateAnchorManuallySmooth(headingId) {
      // 首先清理所有激活状态
      this.clearAllActiveStates()

      // 添加当前激活状态
      const allLinks = document.querySelectorAll('.section-nav .ant-anchor-link')
      allLinks.forEach(el => {
        const titleEl = el.querySelector('.ant-anchor-link-title')
        const href = titleEl?.getAttribute('href')
        if (href === `#${headingId}`) {
          el.classList.add('ant-anchor-link-active')
        }
      })

      // 指示器已隐藏，无需更新位置
      // requestAnimationFrame(() => {
      //   this.updateIndicatorPositionSmooth(headingId)
      // })

      if (process.env.NODE_ENV === 'development') {
        console.log(`🎯 滚动平滑更新: ${headingId}`)
      }
    },

    // 只更新文本激活状态（不包含指示器）
    updateAnchorTextStateOnly(headingId) {
      // 首先清理所有激活状态
      this.clearAllActiveStates()

      // 添加当前激活状态
      const allLinks = document.querySelectorAll('.section-nav .ant-anchor-link')
      allLinks.forEach(el => {
        const titleEl = el.querySelector('.ant-anchor-link-title')
        const href = titleEl?.getAttribute('href')
        if (href === `#${headingId}`) {
          el.classList.add('ant-anchor-link-active')
        }
      })

      if (process.env.NODE_ENV === 'development') {
        console.log(`📝 文本状态更新: ${headingId}`)
      }
    },

    // 更新指示器位置（小蓝点）
    updateIndicatorPosition(headingId) {
      // 查找激活的链接元素
      const activeLinkContainer = document.querySelector(`.section-nav .ant-anchor-link a[href="#${headingId}"]`)?.closest('.ant-anchor-link')
      const anchorWrapper = document.querySelector('.section-nav .ant-anchor-wrapper')
      const indicator = document.querySelector('.section-nav .ant-anchor-ink')
      const indicatorBall = document.querySelector('.section-nav .ant-anchor-ink-ball')

      if (activeLinkContainer && anchorWrapper && indicator && indicatorBall) {
        // 计算激活链接相对于锚点容器的位置
        const linkRect = activeLinkContainer.getBoundingClientRect()
        const wrapperRect = anchorWrapper.getBoundingClientRect()

        // 计算链接中心点相对于容器顶部的距离
        const linkCenterY = linkRect.top - wrapperRect.top + (linkRect.height / 2)

        // 考虑容器的padding和滚动偏移
        const containerPadding = parseInt(getComputedStyle(anchorWrapper).paddingTop) || 0
        const finalPosition = linkCenterY + containerPadding

        // 平滑更新指示器位置
        indicatorBall.style.transform = `translateY(${finalPosition}px) translateX(-50%)`
        indicatorBall.style.opacity = '1'
        indicatorBall.style.visibility = 'visible'
        indicatorBall.classList.add('visible')

        if (process.env.NODE_ENV === 'development') {
          console.log(`🔵 指示器位置更新: ${headingId}, position: ${finalPosition}px`)
        }
      } else if (process.env.NODE_ENV === 'development') {
        console.warn(`⚠️ 无法找到指示器相关元素`)
        console.warn(`link: ${!!activeLinkContainer}, wrapper: ${!!anchorWrapper}, indicator: ${!!indicator}, ball: ${!!indicatorBall}`)
      }
    },

    // 平滑更新指示器位置（滚动专用，避免跳跃）
    updateIndicatorPositionSmooth(headingId) {
      // 查找激活的链接元素
      const activeLinkContainer = document.querySelector(`.section-nav .ant-anchor-link a[href="#${headingId}"]`)?.closest('.ant-anchor-link')
      const anchorWrapper = document.querySelector('.section-nav .ant-anchor-wrapper')
      const indicator = document.querySelector('.section-nav .ant-anchor-ink')
      const indicatorBall = document.querySelector('.section-nav .ant-anchor-ink-ball')

      if (activeLinkContainer && anchorWrapper && indicator && indicatorBall) {
        // 计算激活链接相对于锚点容器的位置
        const linkRect = activeLinkContainer.getBoundingClientRect()
        const wrapperRect = anchorWrapper.getBoundingClientRect()

        // 计算链接中心点相对于容器顶部的距离
        const linkCenterY = linkRect.top - wrapperRect.top + (linkRect.height / 2)

        // 考虑容器的padding
        const containerPadding = parseInt(getComputedStyle(anchorWrapper).paddingTop) || 0
        const finalPosition = linkCenterY + containerPadding

        // 获取当前位置，实现平滑过渡
        const currentTransform = indicatorBall.style.transform
        const currentY = currentTransform.match(/translateY\(([^)]+)\)/)?.[1] || '0px'
        const currentPosition = parseFloat(currentY)

        // 如果位置变化很小，跳过更新避免抖动
        if (Math.abs(finalPosition - currentPosition) < 2) {
          return
        }

        // 平滑更新指示器位置
        indicatorBall.style.transform = `translateY(${finalPosition}px) translateX(-50%)`
        indicatorBall.style.opacity = '1'
        indicatorBall.style.visibility = 'visible'
        indicatorBall.classList.add('visible')

        if (process.env.NODE_ENV === 'development') {
          console.log(`🔵 平滑指示器位置更新: ${headingId}, ${currentPosition}px → ${finalPosition}px`)
        }
      }
    },

    // 清理所有导航激活状态（防止多重激活）
    clearAllActiveStates() {
      if (this.$isServer) return

      // 更高效的状态清理：一次性清理所有相关类名
      const sectionNav = document.querySelector('.section-nav')
      if (!sectionNav) return

      // 移除所有可能的激活状态类名
      const allActiveElements = sectionNav.querySelectorAll('.ant-anchor-link-active, .ant-anchor-link-title-active, .active')
      allActiveElements.forEach(element => {
        element.classList.remove('ant-anchor-link-active', 'ant-anchor-link-title-active', 'active')
      })

      // 额外保险：清理所有链接元素
      const allLinks = sectionNav.querySelectorAll('.ant-anchor-link')
      allLinks.forEach(link => {
        link.classList.remove('ant-anchor-link-active', 'ant-anchor-link-title-active')
      })

      // 指示器已隐藏，无需清理
      // const indicatorBall = sectionNav.querySelector('.ant-anchor-ink-ball')
      // if (indicatorBall) {
      //   indicatorBall.classList.remove('visible')
      // }

      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 清理所有激活状态完成')
      }
    },

    // 处理右侧导航点击事件
    handleAnchorClick(e, link) {
      if (this.$isServer) return

      // 阻止 ant-anchor 的默认滚动行为
      e.preventDefault()

      // 提取目标标题ID
      const href = link.href
      const headingId = href.replace('#', '')

      // 防抖处理：如果点击间隔太短，延迟处理
      const currentTime = Date.now()
      if (currentTime - this.lastClickTime < this.clickDebounceDelay) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏸️ 频繁点击检测，延迟处理右侧导航')
        }

        // 清除之前的定时器
        if (this.navigationTimer) {
          clearTimeout(this.navigationTimer)
        }

        // 延迟执行真正的点击处理
        this.navigationTimer = setTimeout(() => {
          this.performAnchorNavigation(headingId)
        }, this.clickDebounceDelay)
        return
      }

      this.lastClickTime = currentTime
      this.performAnchorNavigation(headingId)
    },

    // 执行实际的锚点导航操作
    performAnchorNavigation(headingId) {
      const element = document.getElementById(headingId)
      if (!element) return

      if (process.env.NODE_ENV === 'development') {
        console.log(`🖱️ 用户点击导航: ${headingId}`)
      }

      // 清除所有之前的定时器和状态
      if (this.navigationTimer) {
        clearTimeout(this.navigationTimer)
        this.navigationTimer = null
      }

      // 立即更新激活状态，避免延迟
      this.activeHeading = headingId
      this.isNavigating = true

      // 立即清理并更新视觉状态
      this.clearAllActiveStates()
      this.updateAnchorActiveState(headingId)

      // 添加滚动逻辑 - 使用精确的滚动控制避免抖动
      const elementRect = element.getBoundingClientRect()
      const currentScrollTop = window.scrollY || document.documentElement.scrollTop
      const elementTop = elementRect.top + currentScrollTop

      // 使用固定的偏移量，确保标题不会被页头遮挡
      const fixedOffset = 80 // 考虑页头高度
      const targetScrollTop = elementTop - fixedOffset

      // 平滑滚动到目标位置
      window.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      })

      // 设置较短的恢复时间，减少状态锁定时间
      this.navigationTimer = setTimeout(() => {
        this.isNavigating = false
        this.navigationTimer = null
        // 最终确保状态正确
        this.updateAnchorActiveState(headingId)

        if (process.env.NODE_ENV === 'development') {
          console.log(`🏁 导航点击处理完成: ${headingId}`)
        }
      }, 800)
    }
  },

  metaInfo() {
    const title = this.globalDataArray?.siteInfo?.title || ''
    const description = this.globalDataArray?.siteInfo?.description || ''
    const keywords = this.globalDataArray?.siteInfo?.keywords || ''
    return {
      title: title,
      meta: [
        { vmid: 'description', name: 'description', content: description },
        { vmid: 'keywords', name: 'keywords', content: keywords }
      ]
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
  background: #fff;
  padding-top: 60px; /* 恢复原有的padding */
}

.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000; /* Ensure header is above all other elements */
}

.sidebar {
  position: fixed;
  width: 240px;
  height: calc(100vh - 60px);
  top: 60px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  z-index: 10;
  transition: width 0.3s ease;
}

.sidebar.collapsed {
  width: 48px;
  overflow: hidden;
}

.sidebar-toggle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.2s ease;
}

.sidebar-toggle:hover {
  background: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

.sidebar-content {
  height: 100%;
  overflow-y: auto;
  padding-top: 40px;
}

.main-content {
  flex: 1;
  margin-left: 240px;
  padding: 0 24px;
  background: #fff;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 48px;
}

.main-content.mobile {
  margin-left: 0;
  padding: 0 12px;
}

.content-wrapper {
  display: flex;
  width: 100%;
  gap: 24px;
  min-height: calc(100vh - 60px);
}

.content {
  flex: 1;
  min-width: 0; /* 允许内容区域缩小 */
  padding: 24px 0;
}

/* 移动端本页总览样式 */
.mobile-toc-section {
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  margin-bottom: 16px;
  overflow: hidden;
}

.mobile-toc-title {
  background: #fff;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;
}

.mobile-toc-title:hover {
  background-color: #f5f5f5;
}

.toc-toggle-icon {
  color: #999;
  transition: transform 0.3s ease;
}

.toc-toggle-icon.expanded {
  transform: rotate(180deg);
}

.mobile-toc-container {
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;
  transition: all 0.3s ease;
  opacity: 1;
}

.mobile-toc-container.collapsed {
  max-height: 0;
  padding: 0;
  opacity: 0;
  overflow: hidden;
}

.mobile-toc-item {
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  line-height: 1.4;
  transition: background-color 0.2s ease;
}

.mobile-toc-item:hover {
  background-color: #f0f0f0;
}

.mobile-toc-item:active {
  background-color: #e6f7ff;
}

.mobile-toc-item .toc-text {
  flex: 1;
  font-size: 13px;
  color: #333;
}

.mobile-toc-item.level-1 .toc-text {
  font-weight: 500;
  color: #1890ff;
}

.mobile-toc-item.level-2 {
  padding-left: 24px;
}

.mobile-toc-item.level-2 .toc-text {
  color: #555;
  font-size: 13px;
}

.mobile-toc-item.level-3 {
  padding-left: 36px;
}

.mobile-toc-item.level-3 .toc-text {
  color: #666;
  font-size: 13px;
}

.mobile-toc-item.level-4,
.mobile-toc-item.level-5,
.mobile-toc-item.level-6 {
  padding-left: 48px;
}

.mobile-toc-item.level-4 .toc-text,
.mobile-toc-item.level-5 .toc-text,
.mobile-toc-item.level-6 .toc-text {
  color: #999;
  font-size: 12px;
}

.article-title {
  font-size: 22px;
  font-weight: 500;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 12px;
  padding-top: 35px;
  border-bottom: 1px solid #e8e8e8;
}

.article-content {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.article-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.article-content :deep(table) {
  max-width: 100%;
  display: block;
  overflow-x: auto;
}

.article-content :deep(pre) {
  background: #f5f5f5;
  padding: 12px;
  margin: 16px 0;
  overflow-x: auto;
}

.article-content :deep(code) {
  background: #f5f5f5;
  padding: 2px 4px;
  font-family: monospace;
}

.article-content :deep(h1),
.article-content :deep(h2),
.article-content :deep(h3),
.article-content :deep(h4),
.article-content :deep(h5),
.article-content :deep(h6) {
  color: #333;
  font-weight: 500;
  line-height: 1.5;
  margin: 1em 0 0.5em;
  scroll-margin-top: 80px;
}

.article-content :deep(h2) {
  font-size: 22px;
  font-weight: 500;
  color: #333;
  margin: 28px 0 14px;
}

.article-content :deep(h3) {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 22px 0 12px;
}

.article-content :deep(p) {
  margin-bottom: 16px;
}

.section-nav {
  position: sticky;
  top: 24px;
  width: 180px;
  background: #fff;
  padding: 16px 0;
  align-self: flex-start;
  flex-shrink: 0; /* 防止被压缩 */
  max-height: calc(100vh - 100px); /* 限制最大高度，避免超出视窗 */
  overflow: visible; /* 确保不显示滚动条 */
}

.nav-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  padding: 0 12px 12px; /* 减少左右内边距 */
  margin-bottom: 8px;
}

.nav-wrapper {
  position: relative;
  padding: 0 4px; /* 减少内边距 */
  overflow: visible; /* 确保不显示滚动条 */
  height: auto; /* 自动高度 */
}

:deep(.ant-anchor-wrapper) {
  padding-left: 0 !important;
  background: transparent;
  overflow: visible !important; /* 防止滚动条 */
  height: auto !important; /* 自动高度 */
}

:deep(.ant-anchor) {
  padding-left: 0;
  overflow: visible !important; /* 防止滚动条 */
  height: auto !important; /* 自动高度 */
}

/* 隐藏指示器线条和小蓝点 */
:deep(.ant-anchor-ink) {
  display: none !important; /* 完全隐藏指示器 */
}

:deep(.ant-anchor-ink::before) {
  display: none !important;
}

:deep(.ant-anchor-ink-ball) {
  display: none !important; /* 隐藏小蓝点 */
}

:deep(.ant-anchor-link) {
  padding: 4px 0 4px 12px; /* 减少左内边距 */
  margin-bottom: 2px;
}

:deep(.ant-anchor-link-title) {
  color: #666;
  font-size: 12px; /* 稍微减小字体 */
  line-height: 1.4;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.3s ease;
  padding-right: 8px; /* 确保右侧有间距 */
}

:deep(.ant-anchor-link-title:hover) {
  color: #333;
}

/* 确保激活状态样式的高优先级 */
:deep(.ant-anchor-link-active > .ant-anchor-link-title),
:deep(.ant-anchor-link.ant-anchor-link-active > .ant-anchor-link-title) {
  color: #1890ff !important;
  font-weight: 500 !important;
  transition: all 0.3s ease;
}

/* 额外的激活状态保护 */
.section-nav :deep(.ant-anchor-link-active .ant-anchor-link-title) {
  color: #1890ff !important;
  font-weight: 500 !important;
}

/* 防止导航区域出现滚动条 */
.section-nav,
.section-nav *,
.section-nav :deep(*) {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.section-nav::-webkit-scrollbar,
.section-nav *::-webkit-scrollbar,
.section-nav :deep(*::-webkit-scrollbar) {
  display: none; /* Chrome, Safari, Opera */
}

/* 确保ant-anchor组件不产生滚动条 */
.section-nav :deep(.ant-anchor-wrapper),
.section-nav :deep(.ant-anchor),
.section-nav :deep(.ant-anchor-ink),
.section-nav :deep(.ant-anchor-link) {
  overflow: visible !important;
  max-height: none !important;
  height: auto !important;
}

/* 强制隐藏所有可能的滚动条 */
.section-nav,
.section-nav > *,
.section-nav :deep(> *) {
  overflow-x: hidden !important;
  overflow-y: visible !important;
}

/* 针对具体的ant-design组件 */
:deep(.ant-anchor-wrapper),
:deep(.ant-anchor) {
  overflow: visible !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

:deep(.ant-anchor-wrapper::-webkit-scrollbar),
:deep(.ant-anchor::-webkit-scrollbar) {
  display: none !important;
}

.empty-content {
  text-align: center;
  padding: 60px 0;
  color: #666;
  font-size: 15px;
}

.loading-content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

/* 移动端样式 */
@media (max-width: 1024px) {
  .page-container {
    display: block;
    padding-top: 50px; /* 移动端页头高度 */
  }

  .sidebar {
    display: none;
  }

  .main-content {
    margin-left: 0;
    padding: 0 16px;
  }

  .content-wrapper {
    display: block;
    padding: 0;
    width: 100%;
    gap: 0;
    min-height: calc(100vh - 50px);
  }

  .content {
    padding: 16px 0;
    width: 100%;
    max-width: 100%;
  }

  .article-title {
    font-size: 22px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    padding-top: 35px;
  }

  .article-content {
    font-size: 14px;
  }

  .article-content :deep(h1),
  .article-content :deep(h2),
  .article-content :deep(h3),
  .article-content :deep(h4),
  .article-content :deep(h5),
  .article-content :deep(h6) {
    scroll-margin-top: 80px; /* 移动端调整 */
  }

  .section-nav {
    display: none;
  }
}

/* 中等尺寸屏幕优化 (1025px-1199px) */
@media (min-width: 1025px) and (max-width: 1199px) {
  .sidebar {
    width: 200px;
  }

  .sidebar.collapsed {
    width: 48px;
  }

  .main-content {
    margin-left: 200px;
  }

  .main-content.sidebar-collapsed {
    margin-left: 48px;
  }

  .section-nav {
    display: none; /* 中等尺寸隐藏右侧导航 */
  }
}

/* 大屏幕优化 (1200px-1399px) */
@media (min-width: 1200px) and (max-width: 1399px) {
  .main-content {
    padding: 0 4px; /* 最小化内边距 */
  }

  .content-wrapper {
    max-width: none;
    padding: 0;
    gap: 8px; /* 最小化间距 */
    align-items: flex-start;
  }

  .content {
    flex: 1;
    min-width: 0;
    max-width: calc(100vw - 240px - 160px - 16px); /* 准确计算：视窗宽度 - 侧边栏 - 导航 - 间距 */
    overflow: hidden; /* 防止内容溢出 */
  }

  .section-nav {
    width: 150px; /* 再次减小宽度以适应 */
    flex-shrink: 0;
    overflow: visible !important; /* 防止滚动条 */
    max-height: calc(100vh - 100px); /* 限制最大高度 */
  }
}

/* 超大屏幕 (≥1400px) */
@media (min-width: 1400px) {
  .content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
  }

  .content {
    max-width: 900px;
  }

}
.article-version-info {
  background-color: #f6f7f8;
  padding: 10px 10px;
  font-size: 14px;
  font-weight: bold;
}

/* 添加错误提示样式 */
.error-content {
  text-align: center;
  padding: 40px 20px;
  color: #ff4d4f;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  margin: 20px 0;
}

.error-content::before {
  content: "⚠️";
  display: block;
  font-size: 24px;
  margin-bottom: 12px;
}

/* 添加空目录提示样式 */
.article-content :deep(.empty-directory-content) {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 16px;
}

/* Markdown相关样式 */
.markdown-fallback {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
}

.markdown-fallback pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.markdown-error {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  padding: 16px;
  margin: 16px 0;
}

.markdown-error .error {
  color: #ff4d4f;
  font-weight: 500;
  margin-bottom: 12px;
}

.markdown-error details {
  margin-top: 12px;
}

.markdown-error summary {
  cursor: pointer;
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.markdown-error pre {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}
</style>

